import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { Identifier } from "ra-core";

import { compareByDate, type DateLike } from "@/lib/safeDate";

export type CreatedNote = {
  id: Identifier;
  date?: DateLike;
  contact_id?: Identifier | null;
  deal_id?: Identifier | null;
};

type NoteRow = CreatedNote & Record<string, unknown>;

type ListCache = {
  data?: NoteRow[];
  total?: number;
  pages?: { data?: NoteRow[]; total?: number }[];
};

type ListParams = {
  filter?: Record<string, unknown>;
  sort?: { field?: string; order?: "ASC" | "DESC" };
};

/** Put the saved note back into a list page. An empty date sorts last. */
export function placeCreatedNote<T extends NoteRow>(
  rows: T[],
  note: T,
  order: "ASC" | "DESC" = "DESC",
): T[] {
  const rest = rows.filter((row) => row.id != note.id);
  return [...rest, note].sort((a, b) => compareByDate(a.date, b.date, order));
}

function sameId(left: unknown, right: unknown): boolean {
  return left != null && right != null && String(left) === String(right);
}

function queryMatches(queryKey: QueryKey, resource: string, note: CreatedNote) {
  const [keyResource, type, params] = queryKey as [
    string,
    string,
    ListParams | undefined,
  ];
  if (keyResource !== resource) return false;
  if (type !== "getList" && type !== "getInfiniteList") return false;
  const filter = params?.filter;
  if (!filter) return true;
  if (
    filter.contact_id != null &&
    note.contact_id != null &&
    !sameId(filter.contact_id, note.contact_id)
  ) {
    return false;
  }
  if (
    filter.deal_id != null &&
    note.deal_id != null &&
    !sameId(filter.deal_id, note.deal_id)
  ) {
    return false;
  }
  return true;
}

function sortOrder(queryKey: QueryKey): "ASC" | "DESC" {
  const params = queryKey[2] as ListParams | undefined;
  return params?.sort?.order === "ASC" ? "ASC" : "DESC";
}

function placeInCache(cached: ListCache, note: NoteRow, order: "ASC" | "DESC") {
  if (Array.isArray(cached.pages)) {
    const already = cached.pages.some((page) =>
      page.data?.some((row) => row.id == note.id),
    );
    return {
      ...cached,
      pages: cached.pages.map((page, index) => {
        const data = page.data ?? [];
        if (already) {
          return {
            ...page,
            data: data.map((row) =>
              row.id == note.id ? { ...row, ...note } : row,
            ),
          };
        }
        if (index !== 0) return page;
        return {
          ...page,
          data: placeCreatedNote(data, note, order),
          total: (page.total ?? data.length) + 1,
        };
      }),
    };
  }
  if (!Array.isArray(cached.data)) return cached;
  const added = !cached.data.some((row) => row.id == note.id);
  return {
    ...cached,
    data: placeCreatedNote(cached.data, note, order),
    total: (cached.total ?? cached.data.length) + (added ? 1 : 0),
  };
}

/**
 * A list refetch that started before the insert can finish last and drop the
 * new row. Put that row back on every matching note list, empty date last.
 */
export function keepCreatedNote(
  queryClient: QueryClient,
  resource: string,
  note: CreatedNote,
) {
  if (note?.id == null) return;
  for (const [queryKey, cached] of queryClient.getQueriesData<ListCache>({
    queryKey: [resource],
  })) {
    if (!cached || !queryMatches(queryKey, resource, note)) continue;
    queryClient.setQueryData(
      queryKey,
      placeInCache(cached, note, sortOrder(queryKey)),
    );
  }
}
